import { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import * as Sentry from '@sentry/browser'
import { AuthContext } from '@/context/auth-context'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import signInEmail from '@/firebase/auth/sign-in-email'
import googleAuthRedirect from '@/firebase/auth/google-auth-redirect'
import { authErrorTranslate } from '@/firebase/auth/auth-error-translate'
import { kPaxtonName } from '@/constants/constants-strings'
import { authRedirectPathFrom, signUpPathFor } from '@/auth/utils/auth-redirect'
import { SignInPageContent } from '@/auth/components/SignInPageContent'

export default function SignInPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const user = useContext(AuthContext)?.user
  const redirectPath = authRedirectPathFrom(searchParams)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleSignInInProgress, setGoogleSignInInProgress] = useState(false)

  // Read during render, not from the user object in the effect: linking Google by popup (local
  // and preview builds) upgrades the anonymous user in place, so the object reference never changes.
  const isSignedIn = user != null && !user.isAnonymous

  // Covers every way of becoming signed in on this page: email, the Google popup, and arriving
  // already signed in.
  useEffect(() => {
    if (isSignedIn) {
      navigate(redirectPath ?? '/dashboard', { replace: true })
    }
  }, [navigate, redirectPath, isSignedIn])

  async function handleSignIn(email: string, password: string) {
    setErrorMessage(null)
    setSubmitting(true)
    try {
      await signInEmail(email, password)
    } catch (e) {
      setErrorMessage(e instanceof FirebaseError ? authErrorTranslate(e) : `Unexpected error. ${kPaxtonName} has been notified.`)
      Sentry.captureException(e)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleContinueWithGoogle() {
    setErrorMessage(null)
    setGoogleSignInInProgress(true)
    try {
      // Resolves once the popup flow ends (local and preview builds); the redirect flow leaves the page.
      await googleAuthRedirect()
    } catch (e) {
      Sentry.captureException(e)
    } finally {
      // Success hands over to the auth loader or the navigation above, so the form only comes back
      // when the session is still anonymous: the popup was closed or Google sign-in failed.
      if (getAuth().currentUser?.isAnonymous !== false) {
        setGoogleSignInInProgress(false)
      }
    }
  }

  // The auth loader covers each change of user, and this page shows the same loader in between,
  // so the form only paints once there is an anonymous session to sign in from:
  // - no user yet: right after signing out or on a fresh load, until the anonymous session starts
  // - signed in: until the effect above navigates away
  // - Google sign-in running: until it signs in, or the popup is closed
  if (user == null || isSignedIn || googleSignInInProgress) {
    return <FullPageLoader />
  }

  return (
    <SignInPageContent
      errorMessage={errorMessage}
      submitting={submitting}
      onSignIn={handleSignIn}
      onContinueWithGoogle={() => void handleContinueWithGoogle()}
      onForgotPassword={() => navigate('/forgot-password')}
      onCreateAccount={() => navigate(signUpPathFor(redirectPath))}
    />
  )
}
